import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import Select from 'react-select';
import { Fixture, listFixtures } from '../../../api/tournament/fixturesService';
import { listPlayers } from '../../../api/tournament/playerService';
import { getAllAgeCategoriesPaginated } from '../../../api/tournament/ageCategoryService';
import {
  createCampaignQuestion,
  completeEngagementCampaign,
  correctQuestionResult,
  createVotingCampaign,
  EngagementCampaign,
  EngagementQuestion,
  EngagementQuestionOption,
  getEngagementSetting,
  getVotingCampaign,
  listCampaignQuestions,
  listVotingCampaigns,
  setQuestionResult,
  updateVotingCampaign,
  updateCampaignQuestion,
} from '../../../api/sflTvEngagement/engagementService';
import { formatDateDisplay } from '../../../utils/formatUtils';
import VotingRemoveModal from './VotingRemoveModal';

interface PredictionsManagementProps {
  tournamentId?: string;
  seasonId?: string;
  tournamentSports?: string;
  ageCategoryOptionsList?: { value: string; label: string }[];
}

type DropdownOption = {
  value: string;
  label: string;
  fixture?: Fixture;
};

type PredictionFilterValue = 'tournaments' | 'matches';
type PredictionViewMode = 'list' | 'add' | 'detail' | 'add-match' | 'match-detail';
type PredictionStatus = 'Draft' | 'Scheduled' | 'Upcoming' | 'Live' | 'Locked' | 'Completed';
type PredictionFilterState<T> = Record<PredictionFilterValue, T>;

type PredictionParentState = {
  enabled: boolean;
  parent: EngagementCampaign | null;
  reusableParent: EngagementCampaign | null;
  terminalParents: EngagementCampaign[];
  error: string;
};

type PredictionQuestionItem = {
  id: string;
  campaignId?: string;
  matchId?: string;
  questionText: string;
  startDate: string;
  startClockTime: string;
  lockDate: string;
  lockClockTime: string;
  sortNumber: string;
  optionType: DropdownOption;
  options: string[];
  optionItems: EngagementQuestionOption[];
  questionStatus?: string;
  correctAnswer: string | null;
  correctAnswerLabel: string;
  predictionStatus: PredictionStatus;
  questionEnabled: boolean;
};

type PredictionMatchItem = {
  id: string;
  campaignId?: string;
  team: string;
  matchValue: string;
  ageCategory: string;
  ageCategoryValue: string;
  matchDate: string;
  matchDateValue: string;
  startTime: string;
  endTime: string;
  predictionStatus: PredictionStatus;
};

type PredictionFormData = {
  questionText: string;
  startDate: string;
  startClockTime: string;
  lockDate: string;
  lockClockTime: string;
  sortNumber: string;
  optionType: DropdownOption | null;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
};

type MatchFormData = {
  match: DropdownOption | null;
  ageCategory: DropdownOption | null;
  matchDate: string;
  startTime: string;
  endTime: string;
};

const predictionFilterOptions: DropdownOption[] = [
  { value: 'tournaments', label: 'Tournament' },
  { value: 'matches', label: 'Match' },
];

const optionTypeOptions: DropdownOption[] = [
  { value: 'static', label: 'Static' },
  { value: 'player', label: 'Player' },
];

const emptyFormData: PredictionFormData = {
  questionText: '',
  startDate: '',
  startClockTime: '',
  lockDate: '',
  lockClockTime: '',
  sortNumber: '2',
  optionType: optionTypeOptions[0],
  option1: '',
  option2: '',
  option3: '',
  option4: '',
};

const emptyMatchFormData: MatchFormData = {
  match: null,
  ageCategory: null,
  matchDate: '',
  startTime: '',
  endTime: '',
};

const statusBadgeStyle: Record<PredictionStatus, React.CSSProperties> = {
  Draft: { backgroundColor: '#F8FAFC', color: '#475569', border: '1px solid #CBD5E1' },
  Scheduled: { backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' },
  Upcoming: { backgroundColor: '#FFF7ED', color: '#C2410C', border: '1px solid #FDBA74' },
  Live: { backgroundColor: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' },
  Locked: { backgroundColor: '#F1F5F9', color: '#334155', border: '1px solid #CBD5E1' },
  Completed: { backgroundColor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #93C5FD' },
};

const customSelectStyles = {
  singleValue: (base: any) => ({ ...base, color: '#212529' }),
  control: (base: any) => ({
    ...base,
    minHeight: '44px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    borderColor: '#e2e8f0',
    boxShadow: 'none',
    fontSize: '14px',
    '&:hover': { borderColor: '#cbd5e1' },
  }),
  menu: (base: any) => ({ ...base, zIndex: 9999, borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: '14px',
    backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#f1f5f9' : 'transparent',
    color: state.isSelected ? '#ffffff' : '#334155',
    cursor: 'pointer',
  }),
};

const compactSelectStyles = {
  ...customSelectStyles,
  control: (base: any) => ({
    ...base,
    minHeight: '38px',
    borderRadius: '8px',
    borderColor: '#e5e7eb',
    boxShadow: 'none',
    fontSize: '14px',
    '&:hover': { borderColor: '#cbd5e1' },
  }),
};

const getApiErrorMessage = (error: any, fallback: string): string => error?.response?.data?.message || error?.message || fallback;
const getCampaignId = (campaign?: EngagementCampaign | null): string => campaign?._id ?? campaign?.id ?? '';
const getCampaignSeasonId = (campaign?: EngagementCampaign | null): string => campaign?.seasonId ?? campaign?.config?.seasonId ?? '';
const getCampaignAgeCategoryId = (campaign?: EngagementCampaign | null): string => campaign?.ageCategoryId ?? campaign?.config?.ageCategoryId ?? '';
const isTerminalCampaign = (campaign: EngagementCampaign): boolean => ['archived', 'completed', 'complete'].includes(campaign.status?.toLowerCase() || '');
const isLiveCampaign = (campaign: EngagementCampaign): boolean => ['live', 'active'].includes(campaign.status?.toLowerCase() || '');
const getQuestionId = (question: EngagementQuestion): string => question._id ?? question.id ?? '';

const getCampaignStatus = (campaign?: EngagementCampaign | null): PredictionStatus => {
  const rawStatus = campaign?.status?.toLowerCase();
  if (rawStatus === 'draft') return 'Draft';
  if (rawStatus === 'scheduled') return 'Scheduled';
  if (rawStatus === 'live' || rawStatus === 'active') return 'Live';
  if (rawStatus === 'locked') return 'Locked';
  if (rawStatus === 'completed' || rawStatus === 'complete' || rawStatus === 'archived') return 'Completed';
  return 'Upcoming';
};

const getQuestionStatusForDates = (startTime?: string, lockTime?: string): string => {
  const rawStartMs = startTime ? new Date(startTime).getTime() : Number.NaN;
  const rawLockMs = lockTime ? new Date(lockTime).getTime() : Number.NaN;
  const now = Date.now();

  if (!Number.isNaN(rawStartMs) && rawStartMs > now) return 'scheduled';
  if (!Number.isNaN(rawLockMs) && rawLockMs <= now) return 'locked';
  return 'active';
};

const getQuestionPredictionStatus = (question: EngagementQuestion, campaign: EngagementCampaign): PredictionStatus => {
  const rawStatus = question.status?.toLowerCase();
  if (rawStatus === 'completed' || rawStatus === 'complete') return 'Completed';
  if (rawStatus === 'locked') return 'Locked';

  const config = question.config ?? {};
  const questionStartTime = question.startTime ?? config.startTime;
  const derivedStatus = questionStartTime || question.lockTime
    ? getQuestionStatusForDates(questionStartTime, question.lockTime)
    : rawStatus;

  if (derivedStatus === 'scheduled') return 'Scheduled';
  if (derivedStatus === 'active' || derivedStatus === 'live') return 'Live';
  if (derivedStatus === 'locked') return 'Locked';
  return getCampaignStatus(campaign);
};

const getRawCampaignStatus = (status?: PredictionStatus): string => {
  if (status === 'Draft') return 'draft';
  if (status === 'Scheduled' || status === 'Upcoming') return 'scheduled';
  if (status === 'Live') return 'live';
  if (status === 'Locked') return 'locked';
  if (status === 'Completed') return 'completed';
  return 'draft';
};

const getCampaignScope = (campaign?: EngagementCampaign | null): string => (
  campaign?.scope ?? campaign?.config?.scope ?? campaign?.config?.target ?? ''
).toLowerCase();

const isActivePredictionCampaign = (campaign: EngagementCampaign, scope: 'season' | 'match') => (
  campaign.enabled !== false &&
  campaign.status?.toLowerCase() !== 'archived' &&
  getCampaignScope(campaign) === scope
  && campaign.config?.automationSourceOnly !== true
);

const isSeasonWidePredictionCampaign = (campaign: EngagementCampaign, seasonId: string): boolean => (
  campaign.type === 'prediction' &&
  getCampaignScope(campaign) === 'season' &&
  getCampaignSeasonId(campaign) === seasonId &&
  !getCampaignAgeCategoryId(campaign)
);

const isEnabledPredictionParent = (campaign: EngagementCampaign): boolean => (
  isLiveCampaign(campaign) &&
  campaign.enabled !== false &&
  campaign.config?.autoGenerateMatchCampaigns !== false
);

const getPredictionParentState = (campaigns: EngagementCampaign[], seasonId: string): PredictionParentState => {
  const seasonWideParents = campaigns.filter(campaign => isSeasonWidePredictionCampaign(campaign, seasonId));
  const nonTerminalParents = seasonWideParents.filter(campaign => !isTerminalCampaign(campaign));
  const terminalParents = seasonWideParents.filter(isTerminalCampaign);

  if (nonTerminalParents.length > 1) {
    return {
      enabled: false,
      parent: null,
      reusableParent: null,
      terminalParents,
      error: 'Multiple season prediction parent campaigns exist. Review the campaign setup before using this switch.',
    };
  }

  const parent = nonTerminalParents[0] ?? null;
  return {
    enabled: parent ? isEnabledPredictionParent(parent) : false,
    parent,
    reusableParent: parent && isLiveCampaign(parent) ? parent : null,
    terminalParents,
    error: '',
  };
};

const listAllVotingCampaigns = async (params: Parameters<typeof listVotingCampaigns>[0]): Promise<EngagementCampaign[]> => {
  const firstPage = await listVotingCampaigns({ ...params, page: 1, limit: params?.limit ?? 100 });
  const campaigns = [...(firstPage.campaigns ?? [])];
  let currentPage = firstPage.currentPage || 1;
  let totalPages = firstPage.totalPages || 1;
  let hasNext = Boolean(firstPage.hasNext);

  while (hasNext || currentPage < totalPages) {
    const response = await listVotingCampaigns({ ...params, page: currentPage + 1, limit: params?.limit ?? 100 });
    campaigns.push(...(response.campaigns ?? []));
    currentPage = response.currentPage || currentPage + 1;
    totalPages = response.totalPages || totalPages;
    hasNext = Boolean(response.hasNext);
  }

  return campaigns;
};

const toCampaignDateTime = (date: string, time: string): string => new Date(`${date}T${time || '00:00'}:00`).toISOString();

const toDateInputValue = (value?: string): string => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return '';
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toTimeInputValue = (value?: string): string => {
  if (!value) return '';
  const trimmedValue = value.trim();
  const meridiemMatch = trimmedValue.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  const twentyFourHourMatch = trimmedValue.match(/^(\d{1,2}):(\d{2})/);

  if (meridiemMatch) {
    let hours = Number(meridiemMatch[1]);
    const minutes = meridiemMatch[2];
    const meridiem = meridiemMatch[3].toUpperCase();
    if (meridiem === 'PM' && hours < 12) hours += 12;
    if (meridiem === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }

  if (twentyFourHourMatch) return `${twentyFourHourMatch[1].padStart(2, '0')}:${twentyFourHourMatch[2]}`;

  const parsedDate = new Date(trimmedValue);
  if (Number.isNaN(parsedDate.getTime())) return '';
  return `${String(parsedDate.getHours()).padStart(2, '0')}:${String(parsedDate.getMinutes()).padStart(2, '0')}`;
};

const getFixtureSideName = (side: any, fallbackTeam?: any): string => {
  if (side?.team && typeof side.team === 'object') return side.team.name || side.team.shortName || '';
  if (Array.isArray(side?.players) && side.players.length > 0) return side.players.map((player: any) => player?.name).filter(Boolean).join(' / ');
  return fallbackTeam?.name || fallbackTeam?.shortName || '';
};

const getFixtureMatchLabel = (fixture: Fixture): string => {
  const firstSide = getFixtureSideName((fixture as any).side1 || (fixture as any).team1Info, fixture.teamA);
  const secondSide = getFixtureSideName((fixture as any).side2 || (fixture as any).team2Info, fixture.teamB);
  return [firstSide, secondSide].filter(Boolean).join(' vs ') || `Match ${fixture.matchNum || ''}`.trim();
};

const mapFixtureToMatchOption = (fixture: Fixture): DropdownOption => ({
  value: fixture._id,
  label: getFixtureMatchLabel(fixture),
  fixture,
});

const mapAgeCategoryToOption = (ageCategory: any): DropdownOption | null => {
  const value = ageCategory?._id ?? ageCategory?.id ?? ageCategory?.value ?? '';
  const label = ageCategory?.ageCategory ?? ageCategory?.name ?? ageCategory?.label ?? '';
  return value && label ? { value, label } : null;
};

const getUniqueOptions = (options: DropdownOption[]) => {
  const seen = new Set<string>();
  return options.filter(option => {
    if (seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

const getPlayerName = (player: any): string => {
  const firstName = player?.playerInfo?.firstName ?? player?.firstName ?? player?.firstname ?? '';
  const lastName = player?.playerInfo?.lastName ?? player?.lastName ?? player?.lastname ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  return player?.playerInfo?.name ?? player?.name ?? player?.playerName ?? player?.fullName ?? fullName ?? 'Unnamed Player';
};

const getPlayerId = (player: any): string => player?.playerInfo?._id ?? player?._id ?? player?.id ?? player?.playerId ?? '';

const mapApiPlayerToOption = (player: any): DropdownOption => ({
  value: getPlayerId(player),
  label: getPlayerName(player),
});

const getQuestionOptions = (formData: PredictionFormData) => (
  [formData.option1, formData.option2, formData.option3, formData.option4].filter(option => option.trim())
);

const buildQuestionOptions = (formData: PredictionFormData, playerOptions: DropdownOption[]) => {
  const optionType = formData.optionType?.value || 'static';
  const isPlayerOptionType = optionType === 'player';

  return getQuestionOptions(formData).map((value, index) => {
    const selectedPlayer = isPlayerOptionType
      ? playerOptions.find(option => option.value === value || option.label === value)
      : null;

    return {
      id: isPlayerOptionType ? (selectedPlayer?.value || value) : `${optionType}_${index + 1}`,
      label: isPlayerOptionType ? (selectedPlayer?.label || value) : value,
    };
  });
};

const findOption = (options: DropdownOption[], value?: string, fallbackLabel?: string): DropdownOption | null => {
  if (!value && !fallbackLabel) return null;
  return options.find(option => option.value === value || option.label === fallbackLabel) || (fallbackLabel ? { value: value || fallbackLabel, label: fallbackLabel } : null);
};

const getQuestionResult = (question: EngagementQuestion): string | null => {
  const config = question.config ?? {};
  return question.correctAnswer ?? config.correctAnswerId ?? config.correctAnswer ?? config.answer ?? null;
};

const getQuestionOptionLabel = (options: EngagementQuestionOption[], optionId?: string | null, fallbackLabel?: string): string => {
  if (!optionId && !fallbackLabel) return '';
  return options.find(option => option.id === optionId)?.label ?? fallbackLabel ?? optionId ?? '';
};

const openInputPicker = (event: React.MouseEvent<HTMLElement>) => {
  const input = event.currentTarget as HTMLInputElement & { showPicker?: () => void };
  input.showPicker?.();
};

const mapQuestionToItem = (question: EngagementQuestion, campaign: EngagementCampaign): PredictionQuestionItem => {
  const config = question.config ?? {};
  const optionTypeValue = config.optionType || 'static';
  const questionStartTime = question.startTime ?? config.startTime;
  const derivedQuestionStatus = questionStartTime || question.lockTime
    ? getQuestionStatusForDates(questionStartTime, question.lockTime)
    : undefined;

  const optionItems = Array.isArray(question.options) ? question.options : [];
  const correctAnswer = getQuestionResult(question);
  return {
    id: getQuestionId(question),
    campaignId: getCampaignId(campaign),
    matchId: question.matchId ?? config.matchId,
    questionText: question.question || '',
    startDate: toDateInputValue(questionStartTime),
    startClockTime: toTimeInputValue(questionStartTime),
    lockDate: toDateInputValue(question.lockTime),
    lockClockTime: toTimeInputValue(question.lockTime),
    sortNumber: String(question.sortNo ?? config.sortNo ?? config.sortNumber ?? '2'),
    optionType: findOption(optionTypeOptions, optionTypeValue, config.optionTypeLabel) || optionTypeOptions[0],
    options: optionItems
      ? optionItems.map(option => optionTypeValue === 'player' ? option.id : option.label).filter(Boolean)
      : [],
    optionItems,
    questionStatus: question.status ?? derivedQuestionStatus,
    correctAnswer,
    correctAnswerLabel: getQuestionOptionLabel(optionItems, correctAnswer, config.correctAnswerLabel ?? config.answerLabel),
    predictionStatus: getQuestionPredictionStatus(question, campaign),
    questionEnabled: question.enabled !== false,
  };
};

const mapCampaignToMatch = (campaign: EngagementCampaign): PredictionMatchItem => {
  const config = campaign.config ?? {};
  const matchId = config.matchId || '';
  return {
    id: matchId || getCampaignId(campaign),
    campaignId: getCampaignId(campaign),
    team: config.matchLabel || campaign.name || 'Match',
    matchValue: matchId,
    ageCategory: config.ageCategoryLabel || '',
    ageCategoryValue: config.ageCategoryId || '',
    matchDate: formatDateDisplay(toDateInputValue(campaign.startTime)),
    matchDateValue: toDateInputValue(campaign.startTime),
    startTime: toTimeInputValue(campaign.startTime),
    endTime: toTimeInputValue(campaign.endTime),
    predictionStatus: getCampaignStatus(campaign),
  };
};

const PredictionsManagement: React.FC<PredictionsManagementProps> = ({ tournamentId, seasonId, tournamentSports, ageCategoryOptionsList }) => {
  const [viewMode, setViewMode] = useState<PredictionViewMode>('list');
  const [isPredictionsEnabled, setIsPredictionsEnabled] = useState<boolean>(false);
  const [isSharedPredictionGateBlocked, setIsSharedPredictionGateBlocked] = useState(false);
  const [predictionParentState, setPredictionParentState] = useState<PredictionParentState>({
    enabled: false,
    parent: null,
    reusableParent: null,
    terminalParents: [],
    error: '',
  });
  const [selectedFilter, setSelectedFilter] = useState<DropdownOption>(predictionFilterOptions[0]);
  const [questions, setQuestions] = useState<PredictionFilterState<PredictionQuestionItem[]>>({ tournaments: [], matches: [] });
  const [campaigns, setCampaigns] = useState<PredictionFilterState<EngagementCampaign | null>>({ tournaments: null, matches: null });
  const [matches, setMatches] = useState<PredictionMatchItem[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<PredictionMatchItem | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<PredictionQuestionItem | null>(null);
  const [questionToDelete, setQuestionToDelete] = useState<PredictionQuestionItem | null>(null);
  const [resultQuestion, setResultQuestion] = useState<PredictionQuestionItem | null>(null);
  const [selectedResultOption, setSelectedResultOption] = useState<DropdownOption | null>(null);
  const [resultError, setResultError] = useState('');
  const [isSettingResult, setIsSettingResult] = useState(false);
  const [completingCampaignId, setCompletingCampaignId] = useState('');
  const [isEditingQuestion, setIsEditingQuestion] = useState(false);
  const [formData, setFormData] = useState<PredictionFormData>(emptyFormData);
  const [matchFormData, setMatchFormData] = useState<MatchFormData>(emptyMatchFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [matchFormErrors, setMatchFormErrors] = useState<Record<string, string>>({});
  const [playerOptions, setPlayerOptions] = useState<DropdownOption[]>([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersError, setPlayersError] = useState('');
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState('');
  const [isSavingQuestion, setIsSavingQuestion] = useState(false);
  const [isUpdatingToggle, setIsUpdatingToggle] = useState(false);
  const [apiAgeCategoryOptions, setApiAgeCategoryOptions] = useState<DropdownOption[]>([]);
  const [ageCategoriesError, setAgeCategoriesError] = useState('');
  const [isLoadingAgeCategories, setIsLoadingAgeCategories] = useState(false);
  const [availableMatchFixtures, setAvailableMatchFixtures] = useState<Fixture[]>([]);
  const [isLoadingMatchFixtures, setIsLoadingMatchFixtures] = useState(false);
  const [matchFixturesError, setMatchFixturesError] = useState('');
  const [isSavingMatch, setIsSavingMatch] = useState(false);
  const nextMatchId = useRef(0);

  const activeFilterKey = selectedFilter.value as PredictionFilterValue;
  const selectedSport = tournamentSports || 'football';
  const ageCategoryDropdownOptions = ageCategoryOptionsList && ageCategoryOptionsList.length > 0 ? ageCategoryOptionsList : apiAgeCategoryOptions;
  const matchOptions = useMemo(() => availableMatchFixtures.map(mapFixtureToMatchOption), [availableMatchFixtures]);
  const activeQuestions = activeFilterKey === 'matches' && selectedMatch
    ? questions.matches.filter(question => question.matchId === selectedMatch.matchValue || question.campaignId === selectedMatch.campaignId)
    : questions[activeFilterKey];

  const loadPredictionData = async (activeSeasonId: string) => {
    const [seasonCampaigns, matchCampaigns, settingResponse] = await Promise.all([
      listAllVotingCampaigns({ limit: 100, type: 'prediction', scope: 'season', docType: 'season', seasonId: activeSeasonId, includeInternal: true }),
      listAllVotingCampaigns({ limit: 100, type: 'prediction', scope: 'match', docType: 'match', seasonId: activeSeasonId }),
      getEngagementSetting('season', activeSeasonId).catch(() => null),
    ]);

    const parentState = getPredictionParentState(seasonCampaigns, activeSeasonId);
    const visibleSeasonCampaign = seasonCampaigns.find(campaign => isActivePredictionCampaign(campaign, 'season')) ?? null;
    const visibleMatchCampaigns = matchCampaigns.filter(campaign => isActivePredictionCampaign(campaign, 'match'));
    const visibleSeasonCampaignId = getCampaignId(visibleSeasonCampaign);
    const seasonQuestions = visibleSeasonCampaignId ? await listCampaignQuestions(visibleSeasonCampaignId) : [];
    const matchQuestionGroups = await Promise.all(visibleMatchCampaigns.map(async (campaign) => {
      const campaignId = getCampaignId(campaign);
      const campaignQuestions = campaignId ? await listCampaignQuestions(campaignId) : [];
      return campaignQuestions.map(question => mapQuestionToItem(question, campaign));
    }));

    return {
      parentState,
      visibleSeasonCampaign,
      visibleMatchCampaigns,
      seasonQuestions,
      matchQuestions: matchQuestionGroups.flat().filter(question => question.questionEnabled),
      sharedGateBlocked: settingResponse?.enabled === false,
    };
  };

  const refreshPredictionData = async () => {
    if (!seasonId) return;
    const loadedData = await loadPredictionData(seasonId);
    setPredictionParentState(loadedData.parentState);
    setCampaigns({ tournaments: loadedData.visibleSeasonCampaign, matches: null });
    setQuestions({
      tournaments: loadedData.visibleSeasonCampaign ? loadedData.seasonQuestions.filter(question => question.enabled !== false).map(question => mapQuestionToItem(question, loadedData.visibleSeasonCampaign!)) : [],
      matches: loadedData.matchQuestions,
    });
    setMatches(loadedData.visibleMatchCampaigns.map(mapCampaignToMatch));
    setIsPredictionsEnabled(loadedData.parentState.enabled);
    setIsSharedPredictionGateBlocked(loadedData.sharedGateBlocked);
  };

  useEffect(() => {
    let isMounted = true;

    const loadAgeCategories = async () => {
      if (!tournamentId || (ageCategoryOptionsList && ageCategoryOptionsList.length > 0)) return;
      setIsLoadingAgeCategories(true);
      setAgeCategoriesError('');
      try {
        const response = await getAllAgeCategoriesPaginated(tournamentId, 1, 100, '', '', '', seasonId, selectedSport);
        if (!isMounted) return;
        const ageCategories = response?.paginatedResults ?? response?.results ?? response?.data ?? (Array.isArray(response) ? response : []);
        setApiAgeCategoryOptions(getUniqueOptions(ageCategories.map(mapAgeCategoryToOption).filter((option: DropdownOption | null): option is DropdownOption => Boolean(option))));
      } catch (error: any) {
        if (!isMounted) return;
        setAgeCategoriesError(getApiErrorMessage(error, 'Failed to load age categories.'));
      } finally {
        if (isMounted) setIsLoadingAgeCategories(false);
      }
    };

    loadAgeCategories();

    return () => {
      isMounted = false;
    };
  }, [ageCategoryOptionsList, seasonId, tournamentId, selectedSport]);

  useEffect(() => {
    let isMounted = true;

    const loadPredictions = async () => {
      if (!seasonId) {
        setIsPredictionsEnabled(false);
        setIsSharedPredictionGateBlocked(false);
        setPredictionParentState({ enabled: false, parent: null, reusableParent: null, terminalParents: [], error: '' });
        setQuestions({ tournaments: [], matches: [] });
        setMatches([]);
        return;
      }

      setIsLoadingQuestions(true);
      setQuestionsError('');
      try {
        const loadedData = await loadPredictionData(seasonId);

        if (!isMounted) return;

        setPredictionParentState(loadedData.parentState);
        setCampaigns({ tournaments: loadedData.visibleSeasonCampaign, matches: null });
        setQuestions({
          tournaments: loadedData.visibleSeasonCampaign ? loadedData.seasonQuestions.filter(question => question.enabled !== false).map(question => mapQuestionToItem(question, loadedData.visibleSeasonCampaign!)) : [],
          matches: loadedData.matchQuestions,
        });
        setMatches(loadedData.visibleMatchCampaigns.map(mapCampaignToMatch));
        setIsPredictionsEnabled(loadedData.parentState.enabled);
        setIsSharedPredictionGateBlocked(loadedData.sharedGateBlocked);
      } catch (error: any) {
        if (!isMounted) return;
        setIsPredictionsEnabled(false);
        setPredictionParentState(previous => ({ ...previous, enabled: false, error: getApiErrorMessage(error, 'Failed to load prediction campaign state.') }));
        setQuestionsError(getApiErrorMessage(error, 'Failed to load predictions.'));
      } finally {
        if (isMounted) setIsLoadingQuestions(false);
      }
    };

    loadPredictions();

    return () => {
      isMounted = false;
    };
  }, [seasonId]);

  useEffect(() => {
    if (viewMode === 'list' || formData.optionType?.value !== 'player') return;

    let isMounted = true;
    const loadPlayers = async () => {
      setPlayersLoading(true);
      setPlayersError('');
      try {
        const playersResponse = await listPlayers({
          page: 1,
          pageSize: 500,
          tournament: tournamentId || '',
          tournamentSeason: seasonId || undefined,
          sport: selectedSport,
        });
        if (!isMounted) return;
        const rawPlayers = Array.isArray(playersResponse?.paginatedResults)
          ? playersResponse.paginatedResults
          : Array.isArray(playersResponse?.data?.paginatedResults)
            ? playersResponse.data.paginatedResults
            : Array.isArray(playersResponse)
              ? playersResponse
              : [];
        setPlayerOptions(rawPlayers.map(mapApiPlayerToOption).filter((option: DropdownOption) => option.value));
      } catch (error: any) {
        if (!isMounted) return;
        setPlayersError(getApiErrorMessage(error, 'Failed to load players.'));
        setPlayerOptions([]);
      } finally {
        if (isMounted) setPlayersLoading(false);
      }
    };

    loadPlayers();

    return () => {
      isMounted = false;
    };
  }, [formData.optionType?.value, seasonId, tournamentId, selectedSport, viewMode]);

  useEffect(() => {
    let isMounted = true;

    const loadMatchFixtures = async () => {
      if (activeFilterKey !== 'matches' || viewMode !== 'add-match' || !tournamentId || !matchFormData.ageCategory?.value) {
        setAvailableMatchFixtures([]);
        setMatchFixturesError('');
        return;
      }

      setIsLoadingMatchFixtures(true);
      setMatchFixturesError('');
      try {
        const response = await listFixtures({
          tournamentId,
          tournamentSeason: seasonId,
          sport: selectedSport,
          ageCategory: matchFormData.ageCategory.value,
          page: 1,
          pageSize: 500,
        });
        if (!isMounted) return;
        setAvailableMatchFixtures(response?.paginatedResults ?? []);
      } catch (error: any) {
        if (!isMounted) return;
        setAvailableMatchFixtures([]);
        setMatchFixturesError(getApiErrorMessage(error, 'Failed to load matches.'));
      } finally {
        if (isMounted) setIsLoadingMatchFixtures(false);
      }
    };

    loadMatchFixtures();

    return () => {
      isMounted = false;
    };
  }, [activeFilterKey, matchFormData.ageCategory?.value, seasonId, tournamentId, selectedSport, viewMode]);

  const resetForm = () => {
    setFormData(emptyFormData);
    setFormErrors({});
  };

  const resetMatchForm = () => {
    setMatchFormData(emptyMatchFormData);
    setMatchFormErrors({});
  };

  const handleInputChange = (field: keyof PredictionFormData, value: string | DropdownOption | null) => {
    setFormData(previous => {
      const updated = { ...previous, [field]: value };
      return updated;
    });
    if (formErrors[field]) {
      setFormErrors(previous => {
        const updated = { ...previous };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleMatchInputChange = (field: keyof MatchFormData, value: any) => {
    setMatchFormData(previous => {
      if (field === 'ageCategory') return { ...previous, ageCategory: value, match: null, matchDate: '', startTime: '', endTime: '' };
      if (field === 'match') {
        const fixture = value?.fixture as Fixture | undefined;
        return {
          ...previous,
          match: value,
          matchDate: toDateInputValue(fixture?.date || fixture?.dateTime || fixture?.startTime),
          startTime: toTimeInputValue(fixture?.startTime || fixture?.dateTime || fixture?.time),
          endTime: toTimeInputValue(fixture?.endTime),
        };
      }
      return { ...previous, [field]: value };
    });
    setMatchFormErrors(previous => {
      const updated = { ...previous };
      delete updated[field];
      if (field === 'ageCategory') {
        delete updated.match;
        delete updated.matchDate;
        delete updated.startTime;
        delete updated.endTime;
      }
      if (field === 'match') {
        delete updated.matchDate;
        delete updated.startTime;
        delete updated.endTime;
      }
      return updated;
    });
  };

  const handleCancel = () => {
    resetForm();
    setSelectedQuestion(null);
    setIsEditingQuestion(false);
    setViewMode(activeFilterKey === 'matches' && selectedMatch ? 'match-detail' : 'list');
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.questionText.trim()) errors.questionText = 'Question text is required.';
    if (!formData.startDate) errors.startDate = 'Start date is required.';
    if (!formData.startClockTime) errors.startClockTime = 'Start time is required.';
    if (!formData.lockDate) errors.lockDate = 'Lock date is required.';
    if (!formData.lockClockTime) errors.lockClockTime = 'Lock time is required.';
    if (!formData.sortNumber.trim()) errors.sortNumber = 'Prediction sort number is required.';
    if (!formData.optionType) errors.optionType = 'Option type is required.';
    if (!formData.option1.trim()) errors.option1 = 'Option 1 is required.';
    if (!formData.option2.trim()) errors.option2 = 'Option 2 is required.';
    if (formData.optionType?.value === 'player') {
      const seenPlayerOptions = new Set<string>();
      (['option1', 'option2', 'option3', 'option4'] as const).forEach(field => {
        const value = formData[field].trim();
        if (!value) return;
        const playerId = playerOptions.find(option => option.value === value || option.label === value)?.value || value;
        if (seenPlayerOptions.has(playerId)) {
          errors[field] = 'Player already selected.';
          return;
        }
        seenPlayerOptions.add(playerId);
      });
    }
    if (activeFilterKey === 'matches' && !selectedMatch) errors.submit = 'Select a match before adding question.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleMatchSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors: Record<string, string> = {};
    if (!matchFormData.ageCategory) errors.ageCategory = 'Age category is required.';
    if (!matchFormData.match) errors.match = 'Match is required.';
    if (!matchFormData.matchDate) errors.matchDate = 'Match date is required.';
    if (!matchFormData.startTime) errors.startTime = 'Match starting time is required.';
    if (!matchFormData.endTime) errors.endTime = 'Match ending time is required.';
    if (Object.keys(errors).length > 0) {
      setMatchFormErrors(errors);
      return;
    }

    setIsSavingMatch(true);
    try {
      const matchId = matchFormData.match?.value || '';
      const createdCampaign = await createVotingCampaign({
        name: matchFormData.match?.label || '',
        type: 'prediction',
        scope: 'match',
        seasonId,
        tournamentId,
        ageCategoryId: matchFormData.ageCategory?.value || '',
        matchId,
        config: {
          target: 'match',
          tournamentId,
          seasonId,
          matchId,
          matchLabel: matchFormData.match?.label || '',
          ageCategoryId: matchFormData.ageCategory?.value || '',
          ageCategoryLabel: matchFormData.ageCategory?.label || '',
        },
        presentation: {},
      });
      const newMatch: PredictionMatchItem = {
        id: matchId || `local-match-${nextMatchId.current++}`,
        campaignId: getCampaignId(createdCampaign),
        team: matchFormData.match?.label || '',
        matchValue: matchId,
        ageCategory: matchFormData.ageCategory?.label || '',
        ageCategoryValue: matchFormData.ageCategory?.value || '',
        matchDate: formatDateDisplay(matchFormData.matchDate),
        matchDateValue: matchFormData.matchDate,
        startTime: matchFormData.startTime,
        endTime: matchFormData.endTime,
        predictionStatus: getCampaignStatus(createdCampaign),
      };
      setMatches(previous => [...previous.filter(match => match.matchValue !== newMatch.matchValue), newMatch]);
      setSelectedMatch(newMatch);
      resetMatchForm();
      setViewMode('match-detail');
    } catch (error: any) {
      setMatchFormErrors(previous => ({ ...previous, submit: getApiErrorMessage(error, 'Failed to create match prediction campaign.') }));
    } finally {
      setIsSavingMatch(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateForm()) return;

    const questionPayload = {
      question: formData.questionText.trim(),
      type: 'single_choice' as const,
      options: buildQuestionOptions(formData, playerOptions),
      scope: activeFilterKey === 'matches' ? 'match' : 'season',
      ...(activeFilterKey === 'matches' && selectedMatch ? { matchId: selectedMatch.matchValue || selectedMatch.id } : {}),
      startTime: toCampaignDateTime(formData.startDate, formData.startClockTime),
      lockTime: toCampaignDateTime(formData.lockDate, formData.lockClockTime),
      sortNo: Number(formData.sortNumber),
      config: {
        target: activeFilterKey === 'matches' ? 'match' : 'tournament',
        tournamentId,
        seasonId,
        ...(activeFilterKey === 'matches' && selectedMatch ? { matchId: selectedMatch.matchValue || selectedMatch.id } : {}),
        optionType: formData.optionType?.value || 'static',
        optionTypeLabel: formData.optionType?.label || 'Static',
      },
    };

    setIsSavingQuestion(true);
    try {
      let campaign = activeFilterKey === 'tournaments' ? campaigns.tournaments : null;
      let campaignId = activeFilterKey === 'matches' ? selectedMatch?.campaignId || '' : getCampaignId(campaign);
      if (!campaignId) {
        const createdCampaign = await createVotingCampaign({
          name: activeFilterKey === 'matches' ? selectedMatch?.team || formData.questionText.trim() : 'Tournament Predictions',
          type: 'prediction',
          scope: activeFilterKey === 'matches' ? 'match' : 'season',
          seasonId,
          tournamentId,
          ...(activeFilterKey === 'matches' && selectedMatch ? {
            ageCategoryId: selectedMatch.ageCategoryValue,
          } : {}),
          ...(activeFilterKey === 'matches' && selectedMatch ? { matchId: selectedMatch.matchValue || selectedMatch.id } : {}),
          config: {
            target: activeFilterKey === 'matches' ? 'match' : 'tournament',
            tournamentId,
            seasonId,
            ...(activeFilterKey === 'matches' && selectedMatch ? {
              matchId: selectedMatch.matchValue || selectedMatch.id,
              matchLabel: selectedMatch.team,
              ageCategoryId: selectedMatch.ageCategoryValue,
              ageCategoryLabel: selectedMatch.ageCategory,
            } : {}),
          },
          presentation: {},
        });
        const createdCampaignId = getCampaignId(createdCampaign);
        campaign = createdCampaign;
        campaignId = getCampaignId(campaign);
      }
      if (!campaignId) throw new Error('Prediction campaign was not created.');

      const savedQuestion = selectedQuestion?.id && !selectedQuestion.id.startsWith('local-')
        ? await updateCampaignQuestion(selectedQuestion.id, questionPayload)
        : await createCampaignQuestion(campaignId, {
          ...questionPayload,
          config: { ...questionPayload.config, campaignId },
        });

      const campaignForRow: EngagementCampaign = campaign || {
        id: campaignId,
        _id: campaignId,
        type: 'prediction',
        scope: activeFilterKey === 'matches' ? 'match' : 'season',
        status: activeFilterKey === 'matches' ? getRawCampaignStatus(selectedMatch?.predictionStatus) : 'live',
        config: activeFilterKey === 'matches' && selectedMatch ? {
          target: 'match',
          tournamentId,
          seasonId,
          matchId: selectedMatch.matchValue || selectedMatch.id,
          matchLabel: selectedMatch.team,
          ageCategoryId: selectedMatch.ageCategoryValue,
          ageCategoryLabel: selectedMatch.ageCategory,
        } : undefined,
      };
      const savedItem = mapQuestionToItem(savedQuestion, campaignForRow);

      setQuestions(previous => ({
        ...previous,
        [activeFilterKey]: selectedQuestion
          ? previous[activeFilterKey].map(question => question.id === selectedQuestion.id ? savedItem : question)
          : [...previous[activeFilterKey], savedItem],
      }));
      if (activeFilterKey === 'tournaments') {
        setCampaigns(previous => ({ ...previous, tournaments: campaignForRow }));
      } else if (selectedMatch && campaignId !== selectedMatch.campaignId) {
        setSelectedMatch({ ...selectedMatch, campaignId });
        setMatches(previous => previous.map(match => match.id === selectedMatch.id ? { ...match, campaignId } : match));
      }
      if (seasonId) await refreshPredictionData();
      handleCancel();
    } catch (error: any) {
      setFormErrors({ submit: getApiErrorMessage(error, 'Failed to save prediction question.') });
    } finally {
      setIsSavingQuestion(false);
    }
  };

  const handlePredictionToggle = async (enabled: boolean) => {
    if (!seasonId) return;
    setIsUpdatingToggle(true);
    setQuestionsError('');
    try {
      const latestSeasonCampaigns = await listAllVotingCampaigns({
        limit: 100,
        type: 'prediction',
        scope: 'season',
        docType: 'season',
        seasonId,
        includeInternal: true,
      });
      const latestParentState = getPredictionParentState(latestSeasonCampaigns, seasonId);

      if (latestParentState.error) {
        setPredictionParentState(latestParentState);
        setIsPredictionsEnabled(false);
        setQuestionsError(latestParentState.error);
        return;
      }

      if (enabled) {
        if (latestParentState.reusableParent) {
          const reusableParentId = getCampaignId(latestParentState.reusableParent);
          const parentConfig = latestParentState.reusableParent.config ?? {};
          await updateVotingCampaign(reusableParentId, parentConfig.autoGenerateMatchCampaigns === false
            ? { enabled: true, config: { ...parentConfig, autoGenerateMatchCampaigns: true } }
            : { enabled: true });
        } else if (latestParentState.parent) {
          setQuestionsError('An existing season prediction parent is not live. Review that campaign before enabling automation.');
          return;
        } else if (latestParentState.terminalParents.length > 0) {
          setQuestionsError('A completed or archived season prediction parent already exists. Review it before creating a replacement.');
          return;
        } else {
          await createVotingCampaign({
            name: `Season ${seasonId} Predictions`,
            type: 'prediction',
            scope: 'season',
            seasonId,
            tournamentId,
            config: {
              automationSourceOnly: true,
              autoGenerateMatchCampaigns: true,
              includeDrawOption: true,
              tournamentId,
              seasonId,
            },
          });
        }
      } else {
        const parent = latestParentState.parent ?? latestParentState.reusableParent;
        const parentId = getCampaignId(parent);
        if (!parentId) {
          await refreshPredictionData();
          return;
        }
        await updateVotingCampaign(parentId, { enabled: false });
      }

      await refreshPredictionData();
    } catch (error: any) {
      try {
        await refreshPredictionData();
      } catch {
        setQuestionsError(getApiErrorMessage(error, 'Failed to update prediction campaign. Refresh to confirm the current state.'));
      }
      setQuestionsError(getApiErrorMessage(error, 'Failed to update prediction campaign.'));
    } finally {
      setIsUpdatingToggle(false);
    }
  };

  const handleQuestionToggle = async (question: PredictionQuestionItem) => {
    const nextEnabled = !question.questionEnabled;
    setQuestions(previous => ({
      ...previous,
      [activeFilterKey]: previous[activeFilterKey].map(item => item.id === question.id ? { ...item, questionEnabled: nextEnabled } : item),
    }));
    if (!question.id || question.id.startsWith('local-')) return;
    try {
      await updateCampaignQuestion(question.id, { enabled: nextEnabled });
    } catch (error: any) {
      setQuestions(previous => ({
        ...previous,
        [activeFilterKey]: previous[activeFilterKey].map(item => item.id === question.id ? { ...item, questionEnabled: question.questionEnabled } : item),
      }));
      setQuestionsError(getApiErrorMessage(error, 'Failed to update question status.'));
    }
  };

  const openQuestion = (question: PredictionQuestionItem, editMode: boolean) => {
    setSelectedQuestion(question);
    setIsEditingQuestion(editMode);
    setFormData({
      questionText: question.questionText,
      startDate: question.startDate,
      startClockTime: question.startClockTime,
      lockDate: question.lockDate,
      lockClockTime: question.lockClockTime,
      sortNumber: question.sortNumber,
      optionType: question.optionType,
      option1: question.options[0] || '',
      option2: question.options[1] || '',
      option3: question.options[2] || '',
      option4: question.options[3] || '',
    });
    setFormErrors({});
    setViewMode('detail');
  };

  const handleDeleteQuestion = async (question: PredictionQuestionItem) => {
    const questionId = question.id;
    try {
      if (questionId && !questionId.startsWith('local-')) await updateCampaignQuestion(questionId, { enabled: false });
      setQuestions(previous => ({ ...previous, [activeFilterKey]: previous[activeFilterKey].filter(question => question.id !== questionId) }));
      setQuestionToDelete(null);
      if (selectedQuestion?.id === questionId) handleCancel();
    } catch (error: any) {
      setQuestionsError(getApiErrorMessage(error, 'Failed to delete question.'));
    }
  };

  const getResultOptions = (question: PredictionQuestionItem): DropdownOption[] => (
    question.optionItems.map(option => ({ value: option.id, label: option.label }))
  );

  const openResultModal = (question: PredictionQuestionItem) => {
    setResultQuestion(question);
    setSelectedResultOption(question.correctAnswer
      ? getResultOptions(question).find(option => option.value === question.correctAnswer) ?? null
      : null);
    setResultError('');
  };

  const closeResultModal = () => {
    if (isSettingResult) return;
    setResultQuestion(null);
    setSelectedResultOption(null);
    setResultError('');
  };

  const updateCampaignQuestionRows = (
    campaign: EngagementCampaign,
    campaignQuestions: EngagementQuestion[],
    campaignId: string,
  ) => {
    const scopeKey: PredictionFilterValue = getCampaignScope(campaign) === 'match' ? 'matches' : 'tournaments';
    const mappedQuestions = campaignQuestions
      .filter(question => question.enabled !== false)
      .map(question => mapQuestionToItem(question, campaign));

    setQuestions(previous => ({
      ...previous,
      [scopeKey]: scopeKey === 'matches'
        ? [...previous.matches.filter(question => question.campaignId !== campaignId), ...mappedQuestions]
        : mappedQuestions,
    }));

    if (scopeKey === 'tournaments') {
      setCampaigns(previous => ({ ...previous, tournaments: campaign }));
    } else {
      setMatches(previous => previous.map(match => match.campaignId === campaignId ? {
        ...match,
        predictionStatus: getCampaignStatus(campaign),
      } : match));
      setSelectedMatch(previous => previous?.campaignId === campaignId ? {
        ...previous,
        predictionStatus: getCampaignStatus(campaign),
      } : previous);
    }

    setSelectedQuestion(previous => {
      if (!previous) return previous;
      return mappedQuestions.find(question => question.id === previous.id) ?? previous;
    });
  };

  const handleSetResult = async () => {
    if (!resultQuestion) return;
    if (!selectedResultOption) {
      setResultError('Select a result before submitting.');
      return;
    }
    if (!resultQuestion.campaignId) {
      setResultError('Campaign is missing for this question.');
      return;
    }

    setIsSettingResult(true);
    setResultError('');
    try {
      if (resultQuestion.questionStatus?.toLowerCase() === 'active') {
        await updateCampaignQuestion(resultQuestion.id, { status: 'locked' });
      }
      const hasExistingResult = Boolean(resultQuestion.correctAnswer);
      if (hasExistingResult) {
        await correctQuestionResult(resultQuestion.id, selectedResultOption.value);
      } else {
        await setQuestionResult(resultQuestion.id, selectedResultOption.value);
      }
      const campaign = await getVotingCampaign(resultQuestion.campaignId);
      const refreshedQuestions = await listCampaignQuestions(resultQuestion.campaignId);
      updateCampaignQuestionRows(campaign, refreshedQuestions, resultQuestion.campaignId);
      setResultQuestion(null);
      setSelectedResultOption(null);
      setResultError('');
    } catch (error: any) {
      setResultError(getApiErrorMessage(error, resultQuestion.correctAnswer ? 'Failed to change result.' : 'Failed to set result.'));
    } finally {
      setIsSettingResult(false);
    }
  };

  const getQuestionsForCampaign = (campaignId?: string): PredictionQuestionItem[] => {
    if (!campaignId) return [];
    return [...questions.tournaments, ...questions.matches].filter(question => question.campaignId === campaignId);
  };

  const canCompleteCampaign = (campaignId?: string): boolean => {
    const campaignQuestions = getQuestionsForCampaign(campaignId);
    return campaignQuestions.length > 0 && campaignQuestions.every(question => Boolean(question.correctAnswer));
  };

  const handleCompleteCampaign = async (campaignId?: string) => {
    if (!campaignId || completingCampaignId) return;
    if (!canCompleteCampaign(campaignId)) {
      setQuestionsError('Set the answer for all prediction questions before marking completed.');
      return;
    }

    setCompletingCampaignId(campaignId);
    setQuestionsError('');
    try {
      const completedCampaign = await completeEngagementCampaign(campaignId);
      const refreshedQuestions = await listCampaignQuestions(campaignId);
      updateCampaignQuestionRows(completedCampaign, refreshedQuestions, campaignId);
    } catch (error: any) {
      setQuestionsError(getApiErrorMessage(error, 'Failed to mark campaign completed.'));
    } finally {
      setCompletingCampaignId('');
    }
  };

  const handleFilterChange = (option: DropdownOption | null) => {
    if (!option) return;
    setSelectedFilter(option);
    setSelectedMatch(null);
    setSelectedQuestion(null);
    resetForm();
    resetMatchForm();
    setViewMode('list');
  };

  const renderBackHeader = (title = 'Predictions', onBack = handleCancel) => (
    <div className="d-flex align-items-center gap-3 mb-3">
      <button onClick={onBack} aria-label="Back to predictions" type="button" className="btn btn-light d-flex align-items-center justify-content-center" style={{ width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fff' }}>
        <i className="ri-arrow-left-line" style={{ fontSize: '20px', color: '#000000' }}></i>
      </button>
      <h4 className="mb-0 fw-bold" style={{ fontSize: '24px', color: '#000000' }}>{title}</h4>
    </div>
  );

  const renderQuestionDeleteModal = () => (
    <VotingRemoveModal
      show={!!questionToDelete}
      title="Delete Question?"
      message="Are you sure you want to delete this question?"
      confirmLabel="Yes, Delete"
      onCancel={() => setQuestionToDelete(null)}
      onConfirm={() => {
        if (questionToDelete) handleDeleteQuestion(questionToDelete);
      }}
    />
  );

  const renderResultModal = () => {
    const resultOptions = resultQuestion ? getResultOptions(resultQuestion) : [];
    const isCompleted = resultQuestion?.questionStatus?.toLowerCase() === 'completed';
    const hasExistingResult = Boolean(resultQuestion?.correctAnswer);
    return (
      <Modal show={!!resultQuestion} onHide={closeResultModal} centered>
        <Modal.Header closeButton={!isSettingResult}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>{hasExistingResult ? 'Change Prediction Result' : 'Set Prediction Result'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3" style={{ color: '#334155', fontSize: '14px' }}>{resultQuestion?.questionText}</div>
          <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Correct Answer</Form.Label>
          <Select
            classNamePrefix="react-select"
            value={selectedResultOption}
            onChange={(option) => {
              setSelectedResultOption(option);
              setResultError('');
            }}
            options={resultOptions}
            isSearchable={false}
            isDisabled={isSettingResult || resultOptions.length === 0}
            placeholder={resultOptions.length ? 'Select Answer' : 'No options available'}
            styles={customSelectStyles}
          />
          {isCompleted && <div className="text-muted small mt-2">Result is already set. Select a different answer to change it.</div>}
          {resultError && <div className="text-danger small mt-2">{resultError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeResultModal} disabled={isSettingResult} style={{ borderRadius: '8px' }}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSetResult}
            disabled={isSettingResult || resultOptions.length === 0}
            style={{ borderRadius: '8px', minWidth: 120 }}
          >
            {isSettingResult ? 'Saving...' : hasExistingResult ? 'Change Result' : 'Set Result'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderQuestionForm = () => {
    const isDetailView = viewMode === 'detail';
    const fieldsDisabled = isDetailView && !isEditingQuestion;
    const isPlayerOptionType = formData.optionType?.value === 'player';
    const getSelectedPlayerOption = (field: 'option1' | 'option2' | 'option3' | 'option4') => playerOptions.find(option => option.value === formData[field] || option.label === formData[field]) || null;
    const getAvailablePlayerOptions = (field: 'option1' | 'option2' | 'option3' | 'option4') => {
      const selectedOtherPlayerIds = new Set(
        (['option1', 'option2', 'option3', 'option4'] as const)
          .filter(optionField => optionField !== field)
          .map(optionField => getSelectedPlayerOption(optionField)?.value || formData[optionField])
          .filter(Boolean)
      );
      return playerOptions.filter(option => !selectedOtherPlayerIds.has(option.value));
    };
    const renderOptionControl = (field: 'option1' | 'option2' | 'option3' | 'option4', placeholder: string, required = false) => (
      isPlayerOptionType ? (
        <Select classNamePrefix="react-select" value={getSelectedPlayerOption(field)} onChange={(option) => handleInputChange(field, option?.value || '')} options={getAvailablePlayerOptions(field)} isSearchable isDisabled={fieldsDisabled || playersLoading} placeholder={playersLoading ? 'Loading players...' : placeholder} styles={customSelectStyles} />
      ) : (
        <Form.Control type="text" placeholder={placeholder} value={formData[field]} onChange={(event) => handleInputChange(field, event.target.value)} isInvalid={required && !!formErrors[field]} style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }} />
      )
    );

    return (
      <>
      <div className="predictions-add-question-container" style={{ padding: '0 5px' }}>
        {renderBackHeader(activeFilterKey === 'matches' && selectedMatch ? selectedMatch.team : 'Predictions')}
        <div className="card border-0" style={{ borderRadius: '12px', backgroundColor: '#ffffff', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06)', border: '1px solid #edf2f7' }}>
          <div className="card-body" style={{ padding: '24px' }}>
            <div className="d-flex justify-content-between align-items-start mb-4">
              <h5 className="mb-0" style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>Add New Question</h5>
              {isDetailView && selectedQuestion && (
                <div className="d-flex gap-2">
                  <Button variant="outline-dark" size="sm" style={{ borderRadius: '8px', minWidth: 70 }} onClick={() => setIsEditingQuestion(true)} disabled={isEditingQuestion}>Edit <i className="ri-pencil-line" aria-hidden="true" /></Button>
                  <Button variant="danger" size="sm" style={{ backgroundColor: '#ff5768', borderColor: '#ff5768', borderRadius: '8px', minWidth: 82 }} onClick={() => setQuestionToDelete(selectedQuestion)}>Delete <i className="ri-delete-bin-line" aria-hidden="true" /></Button>
                </div>
              )}
            </div>
            <Form onSubmit={handleSubmit}>
              <fieldset disabled={fieldsDisabled}>
                <Row className="mb-4 g-4">
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Question Text<span className="text-danger">*</span></Form.Label>
                    <Form.Control type="text" placeholder="Enter Question Text" value={formData.questionText} onChange={(event) => handleInputChange('questionText', event.target.value)} isInvalid={!!formErrors.questionText} style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }} />
                    {formErrors.questionText && <div className="text-danger small mt-1">{formErrors.questionText}</div>}
                  </Col>
	                  <Col md={4}>
	                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Start Date <span className="text-danger">*</span></Form.Label>
	                    <Form.Control aria-label="Start Date" type="date" value={formData.startDate} onChange={(event) => handleInputChange('startDate', event.target.value)} isInvalid={!!formErrors.startDate} style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }} />
	                    {formErrors.startDate && <div className="text-danger small mt-1">{formErrors.startDate}</div>}
	                  </Col>
                    <Col md={4}>
                      <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Start Time <span className="text-danger">*</span></Form.Label>
                      <Form.Control aria-label="Start Time" type="time" value={formData.startClockTime} onClick={openInputPicker} onChange={(event) => handleInputChange('startClockTime', event.target.value)} isInvalid={!!formErrors.startClockTime} style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }} />
                      {formErrors.startClockTime && <div className="text-danger small mt-1">{formErrors.startClockTime}</div>}
                    </Col>
	                </Row>
	                <Row className="mb-4 g-4">
	                  <Col md={4}>
	                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Lock Date <span className="text-danger">*</span></Form.Label>
	                    <Form.Control aria-label="Lock Date" type="date" value={formData.lockDate} onChange={(event) => handleInputChange('lockDate', event.target.value)} isInvalid={!!formErrors.lockDate} style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }} />
	                    {formErrors.lockDate && <div className="text-danger small mt-1">{formErrors.lockDate}</div>}
	                  </Col>
	                  <Col md={4}>
	                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Lock Time <span className="text-danger">*</span></Form.Label>
	                    <Form.Control aria-label="Lock Time" type="time" value={formData.lockClockTime} onClick={openInputPicker} onChange={(event) => handleInputChange('lockClockTime', event.target.value)} isInvalid={!!formErrors.lockClockTime} style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }} />
	                    {formErrors.lockClockTime && <div className="text-danger small mt-1">{formErrors.lockClockTime}</div>}
	                  </Col>
	                  <Col md={4}>
	                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Prediction Sort Number <span className="text-danger">*</span></Form.Label>
	                    <Form.Control type="number" value={formData.sortNumber} onChange={(event) => handleInputChange('sortNumber', event.target.value)} isInvalid={!!formErrors.sortNumber} style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }} />
	                    {formErrors.sortNumber && <div className="text-danger small mt-1">{formErrors.sortNumber}</div>}
	                  </Col>
	                </Row>
	                <Row className="mb-4 g-4">
	                  <Col md={4}>
	                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Option Type <span className="text-danger">*</span></Form.Label>
	                    <Select
                      classNamePrefix="react-select"
                      value={formData.optionType}
                      onChange={(option) => {
                        setFormData(previous => ({ ...previous, optionType: option, option1: '', option2: '', option3: '', option4: '' }));
                        setFormErrors({});
                      }}
                      options={optionTypeOptions}
                      isSearchable={false}
                      isDisabled={fieldsDisabled}
                      styles={customSelectStyles}
                    />
	                    {formErrors.optionType && <div className="text-danger small mt-1">{formErrors.optionType}</div>}
	                    {playersError && isPlayerOptionType && <div className="text-danger small mt-1">{playersError}</div>}
	                  </Col>
	                  <Col md={4}>
	                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Option 1<span className="text-danger">*</span></Form.Label>
	                    {renderOptionControl('option1', isPlayerOptionType ? 'Select Player' : 'Enter Option1', true)}
	                    {formErrors.option1 && <div className="text-danger small mt-1">{formErrors.option1}</div>}
	                  </Col>
	                  <Col md={4}>
	                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Option 2<span className="text-danger">*</span></Form.Label>
	                    {renderOptionControl('option2', isPlayerOptionType ? 'Select Player' : 'Enter Option 2', true)}
                    {formErrors.option2 && <div className="text-danger small mt-1">{formErrors.option2}</div>}
                  </Col>
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Option 3</Form.Label>
                    {renderOptionControl('option3', isPlayerOptionType ? 'Select Player' : 'Enter Option 3')}
                  </Col>
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Option 4</Form.Label>
                    {renderOptionControl('option4', isPlayerOptionType ? 'Select Player' : 'Enter Option 4')}
                  </Col>
                </Row>
              </fieldset>
              {formErrors.submit && <div className="text-danger small mb-3">{formErrors.submit}</div>}
              {(!isDetailView || isEditingQuestion) && (
                <div className="d-flex justify-content-end align-items-center gap-3 pt-2">
                  <Button variant="outline-primary" type="button" disabled={isSavingQuestion} onClick={handleCancel} style={{ minWidth: '110px', height: '42px', borderRadius: '8px', borderColor: '#3b82f6', color: '#2563eb', fontWeight: 500 }}>Cancel</Button>
                  <Button variant="primary" type="submit" disabled={isSavingQuestion} style={{ minWidth: '140px', height: '42px', borderRadius: '8px', backgroundColor: '#0d6efd', borderColor: '#0d6efd', fontWeight: 500 }}>
                    {isSavingQuestion ? 'Saving...' : isDetailView ? 'Save Changes' : 'Add Question'}
                  </Button>
                </div>
              )}
            </Form>
          </div>
        </div>
      </div>
      {renderQuestionDeleteModal()}
      </>
    );
  };

  const renderQuestionsTable = (rows: PredictionQuestionItem[]) => rows.length > 0 ? (
    <div style={{ overflowX: 'auto' }}>
      <Table hover responsive className="align-middle mb-0" style={{ fontSize: '14px' }}>
        <thead>
          <tr style={{ backgroundColor: '#f8fafc' }}>
            {['Question name', 'Lock Time', 'Number of Options', 'Result', 'Prediction Status', 'Question Status', 'Actions'].map((heading) => (
              <th key={heading} style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((question, index) => (
            <tr key={question.id}>
              <td style={{ padding: '14px 16px', color: '#1e293b', fontWeight: 500 }}>{question.questionText}</td>
	              <td style={{ padding: '14px 16px', color: question.lockDate && question.lockClockTime ? '#334155' : '#94a3b8', whiteSpace: 'nowrap' }}>
	                {question.lockDate && question.lockClockTime ? `${formatDateDisplay(question.lockDate)} ${question.lockClockTime}` : 'Not set'}
	              </td>
	              <td style={{ padding: '14px 16px', color: '#334155' }}>{question.options.length}</td>
              <td style={{ padding: '14px 16px', color: question.correctAnswerLabel ? '#334155' : '#94a3b8', whiteSpace: 'nowrap' }}>
                {question.correctAnswerLabel || 'Not selected yet'}
              </td>
              <td style={{ padding: '14px 16px' }}>
                <Badge pill style={{ ...statusBadgeStyle[question.predictionStatus], fontSize: '12px', fontWeight: 500, padding: '5px 12px' }}>{question.predictionStatus}</Badge>
              </td>
              <td style={{ padding: '14px 16px' }}>
                <Form.Check type="switch" id={`question-status-${index}`} checked={question.questionEnabled} onChange={() => handleQuestionToggle(question)} style={{ cursor: 'pointer', marginBottom: 0 }} />
              </td>
              <td style={{ padding: '14px 16px' }}>
                <div className="d-flex align-items-center gap-2">
                  <button className="btn btn-sm p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff' }} title="View" onClick={() => openQuestion(question, false)}>
                    <i className="ri-eye-line" style={{ fontSize: '15px', color: '#64748b' }}></i>
                  </button>
	                  <button className="btn btn-sm p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff' }} title="Edit" onClick={() => openQuestion(question, true)}>
	                    <i className="ri-pencil-line" style={{ fontSize: '15px', color: '#3b82f6' }}></i>
	                  </button>
                  {question.questionEnabled && (
                    <button
                      className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                      style={{ width: '30px', height: '30px', border: '1px solid #bfdbfe', borderRadius: '6px', backgroundColor: '#fff' }}
                      title={question.questionStatus?.toLowerCase() === 'completed' ? 'Change Result' : 'Set Result'}
                      onClick={() => openResultModal(question)}
                      disabled={question.optionItems.length === 0}
                    >
                      <i className="ri-checkbox-circle-line" style={{ fontSize: '15px', color: '#2563eb' }}></i>
                    </button>
                  )}
                  {question.questionStatus?.toLowerCase() === 'completed' && (
                    <span className="badge rounded-pill" style={{ backgroundColor: '#dcfce7', color: '#166534', fontWeight: 500 }}>Result Set</span>
                  )}
	                  <button className="btn btn-sm p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', border: '1px solid #fecaca', borderRadius: '6px', backgroundColor: '#fff' }} title="Delete" onClick={() => setQuestionToDelete(question)}>
	                    <i className="ri-delete-bin-line" style={{ fontSize: '15px', color: '#ef4444' }}></i>
	                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  ) : (
    <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: '380px', padding: '60px 20px' }}>
      <div className="mb-3" style={{ color: '#64748b' }}>
        <i className="ri-trophy-line" style={{ fontSize: '58px' }}></i>
      </div>
      <h5 className="fw-bold mb-2" style={{ color: '#1e293b', fontSize: '18px' }}>No {activeFilterKey === 'matches' ? 'Match' : 'Tournament'} predictions available</h5>
      <p className="text-muted mb-0" style={{ fontSize: '14px', maxWidth: '440px' }}>There are no upcoming predictions at the moment.</p>
    </div>
  );

  if (activeFilterKey === 'matches' && viewMode === 'add-match') {
    const selectedMatchFixture = matchFormData.match?.fixture as Fixture | undefined;
    const hasBackendMatchEndTime = Boolean(toTimeInputValue(selectedMatchFixture?.endTime));
    const isMatchEndTimeDisabled = !matchFormData.match || hasBackendMatchEndTime;

    return (
      <div className="predictions-add-match-container" style={{ padding: '0 5px' }}>
        {renderBackHeader('Predictions', () => { resetMatchForm(); setViewMode('list'); })}
        <div className="card border-0" style={{ borderRadius: '12px', backgroundColor: '#ffffff', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06)', border: '1px solid #edf2f7' }}>
          <div className="card-body" style={{ padding: '24px' }}>
            <h5 className="mb-4" style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>Add New Match</h5>
            <Form onSubmit={handleMatchSubmit}>
              <Row className="mb-4 g-4">
                <Col md={4}>
                  <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Age Category<span className="text-danger">*</span></Form.Label>
                  <Select classNamePrefix="react-select" value={matchFormData.ageCategory} onChange={(option) => handleMatchInputChange('ageCategory', option)} options={ageCategoryDropdownOptions} placeholder={isLoadingAgeCategories ? 'Loading Age Categories...' : 'Select Age Category'} isSearchable isDisabled={isLoadingAgeCategories} styles={customSelectStyles} />
                  {matchFormErrors.ageCategory && <div className="text-danger small mt-1">{matchFormErrors.ageCategory}</div>}
                  {ageCategoriesError && <div className="text-danger small mt-1">{ageCategoriesError}</div>}
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Select Match <span className="text-danger">*</span></Form.Label>
                  <Select classNamePrefix="react-select" value={matchFormData.match} onChange={(option) => handleMatchInputChange('match', option)} options={matchOptions} placeholder={matchFormData.ageCategory ? 'Select Match' : 'Select age category first'} isSearchable={false} isDisabled={!matchFormData.ageCategory || isLoadingMatchFixtures} isLoading={isLoadingMatchFixtures} styles={customSelectStyles} />
                  {matchFormErrors.match && <div className="text-danger small mt-1">{matchFormErrors.match}</div>}
                  {matchFixturesError && <div className="text-danger small mt-1">{matchFixturesError}</div>}
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Match Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control type="date" value={matchFormData.matchDate} disabled isInvalid={!!matchFormErrors.matchDate} style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#f8fafc' }} />
                  {matchFormErrors.matchDate && <div className="text-danger small mt-1">{matchFormErrors.matchDate}</div>}
                </Col>
              </Row>
              <Row className="mb-4 g-4">
                <Col md={4}>
                  <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Match Starting Time <span className="text-danger">*</span></Form.Label>
                  <Form.Control type="time" value={matchFormData.startTime} disabled isInvalid={!!matchFormErrors.startTime} style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#f8fafc' }} />
                  {matchFormErrors.startTime && <div className="text-danger small mt-1">{matchFormErrors.startTime}</div>}
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>Match Ending Time <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="time"
                    value={matchFormData.endTime}
                    onChange={(event) => handleMatchInputChange('endTime', event.target.value)}
                    disabled={isMatchEndTimeDisabled}
                    isInvalid={!!matchFormErrors.endTime}
                    style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: isMatchEndTimeDisabled ? '#f8fafc' : '#ffffff' }}
                  />
                  {matchFormErrors.endTime && <div className="text-danger small mt-1">{matchFormErrors.endTime}</div>}
                </Col>
              </Row>
              {matchFormErrors.submit && <div className="text-danger small mb-3">{matchFormErrors.submit}</div>}
              <div className="d-flex justify-content-end align-items-center gap-3 pt-2">
                <Button variant="outline-primary" type="button" disabled={isSavingMatch} onClick={() => { resetMatchForm(); setViewMode('list'); }} style={{ minWidth: '110px', height: '42px', borderRadius: '8px', borderColor: '#3b82f6', color: '#2563eb', fontWeight: 500 }}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={isSavingMatch} style={{ minWidth: '140px', height: '42px', borderRadius: '8px', backgroundColor: '#0d6efd', borderColor: '#0d6efd', fontWeight: 500 }}>{isSavingMatch ? 'Saving...' : 'Add Match'}</Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    );
  }

  if (activeFilterKey === 'matches' && viewMode === 'match-detail' && selectedMatch) {
    const canCompleteSelectedMatch = canCompleteCampaign(selectedMatch.campaignId);

    return (
      <>
      <div className="predictions-match-detail-container" style={{ padding: '0 5px' }}>
        {renderBackHeader(selectedMatch.team, () => { setSelectedMatch(null); setViewMode('list'); })}
        <div className="card border-0" style={{ borderRadius: '12px', backgroundColor: '#ffffff', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06)', border: '1px solid #edf2f7' }}>
          <div className="card-body" style={{ padding: '24px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0" style={{ fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>Questions</h5>
              <div className="d-flex align-items-center gap-2">
                {selectedMatch.campaignId && selectedMatch.predictionStatus === 'Live' && (
                  <Button
                    variant="outline-success"
                    onClick={() => handleCompleteCampaign(selectedMatch.campaignId)}
                    disabled={completingCampaignId === selectedMatch.campaignId || !canCompleteSelectedMatch}
                    style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: 500, fontSize: '14px' }}
                  >
                    {completingCampaignId === selectedMatch.campaignId ? 'Completing...' : 'Mark Completed'}
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={() => {
                    setFormData({
                      ...emptyFormData,
                      startDate: selectedMatch.matchDateValue,
                      startClockTime: selectedMatch.startTime,
                      lockDate: selectedMatch.matchDateValue,
                      lockClockTime: selectedMatch.startTime,
                    });
                    setFormErrors({});
                    setViewMode('add');
                  }}
                  disabled={selectedMatch.predictionStatus === 'Completed' || completingCampaignId === selectedMatch.campaignId}
                  style={{ backgroundColor: '#0d6efd', borderColor: '#0d6efd', borderRadius: '8px', padding: '8px 20px', fontWeight: 500, fontSize: '14px' }}
                >Add Question</Button>
              </div>
	            </div>
            {renderQuestionsTable(activeQuestions)}
          </div>
        </div>
	      </div>
	      {renderQuestionDeleteModal()}
      {renderResultModal()}
	      </>
    );
  }

  if (viewMode === 'add' || viewMode === 'detail') return renderQuestionForm();

  const tournamentCampaignId = getCampaignId(campaigns.tournaments);
  const tournamentPredictionStatus = getCampaignStatus(campaigns.tournaments);
  const isTournamentPredictionCompleted = activeFilterKey === 'tournaments' && tournamentPredictionStatus === 'Completed';
  const isCompletingTournamentPrediction = activeFilterKey === 'tournaments' && Boolean(tournamentCampaignId) && completingCampaignId === tournamentCampaignId;
  const canCompleteTournamentPrediction = canCompleteCampaign(tournamentCampaignId);
  const isAddPredictionActionDisabled = activeFilterKey === 'matches'
    ? false
    : isTournamentPredictionCompleted || isCompletingTournamentPrediction;

  return (
    <>
    <div className="predictions-management-container" style={{ padding: '0 5px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0 fw-bold" style={{ fontSize: '24px', color: '#0f172a' }}>Predictions</h4>
          <Form.Check type="switch" id="predictions-status-switch" checked={isPredictionsEnabled} disabled={isUpdatingToggle || isLoadingQuestions || Boolean(predictionParentState.error)} onChange={(event) => handlePredictionToggle(event.target.checked)} style={{ transform: 'scale(1.15)', cursor: 'pointer', marginBottom: 0 }} />
          {isUpdatingToggle && <span className="text-muted small">Saving...</span>}
          {!isUpdatingToggle && isLoadingQuestions && <span className="text-muted small">Loading...</span>}
        </div>
        <div style={{ minWidth: '140px' }}>
          <Select classNamePrefix="react-select" value={selectedFilter} onChange={handleFilterChange} options={predictionFilterOptions} isSearchable={false} styles={compactSelectStyles} />
        </div>
      </div>
      {(predictionParentState.error || questionsError) && <div className="text-danger small mb-3">{predictionParentState.error || questionsError}</div>}
      {isSharedPredictionGateBlocked && <div className="text-warning small mb-3">Shared season engagement is disabled and may block prediction submissions or campaign creation.</div>}
      <div className="card border-0" style={{ borderRadius: '12px', backgroundColor: '#ffffff', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04)', border: '1px solid #edf2f7' }}>
        <div className="card-body" style={{ padding: '24px' }}>
	          <div className="d-flex justify-content-between align-items-center mb-4">
	            <h5 className="mb-0" style={{ fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>{activeFilterKey === 'matches' ? 'Matches' : 'Questions'}</h5>
              <div className="d-flex align-items-center gap-2">
                {activeFilterKey === 'tournaments' && tournamentCampaignId && tournamentPredictionStatus === 'Live' && (
                  <Button
                    variant="outline-success"
                    onClick={() => handleCompleteCampaign(tournamentCampaignId)}
                    disabled={isCompletingTournamentPrediction || !canCompleteTournamentPrediction}
                    style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: 500, fontSize: '14px' }}
                  >
                    {isCompletingTournamentPrediction ? 'Completing...' : 'Mark Completed'}
                  </Button>
                )}
                <Button variant="primary" onClick={() => { activeFilterKey === 'matches' ? setViewMode('add-match') : setViewMode('add'); resetForm(); resetMatchForm(); }} disabled={isAddPredictionActionDisabled} style={{ backgroundColor: '#0d6efd', borderColor: '#0d6efd', borderRadius: '8px', padding: '8px 20px', fontWeight: 500, fontSize: '14px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}>
                  {activeFilterKey === 'matches' ? 'Add Match' : 'Add Question'}
                </Button>
              </div>
	          </div>
          {isLoadingQuestions ? (
            <div className="text-center text-muted" style={{ padding: '60px 20px', fontSize: '14px' }}>Loading predictions...</div>
          ) : activeFilterKey === 'matches' ? (
            matches.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <Table hover responsive className="align-middle mb-0" style={{ fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      {['Team', 'Age Category', 'Match Date', 'Number of Questions', 'Prediction Status', 'Actions'].map((heading) => (
                        <th key={heading} style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{heading}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((match) => {
                      const questionCount = questions.matches.filter(question => question.matchId === match.matchValue || question.campaignId === match.campaignId).length;
                      const canCompleteMatchPrediction = canCompleteCampaign(match.campaignId);
                      return (
                        <tr key={match.id}>
                          <td style={{ padding: '14px 16px', color: '#1e293b', fontWeight: 500 }}>{match.team}</td>
                          <td style={{ padding: '14px 16px', color: '#334155' }}>{match.ageCategory}</td>
                          <td style={{ padding: '14px 16px', color: '#334155' }}>{match.matchDate}</td>
                          <td style={{ padding: '14px 16px', color: '#334155' }}>{questionCount}</td>
                          <td style={{ padding: '14px 16px' }}><Badge pill style={{ ...statusBadgeStyle[match.predictionStatus], fontSize: '12px', fontWeight: 500, padding: '5px 12px' }}>{match.predictionStatus}</Badge></td>
                          <td style={{ padding: '14px 16px' }}>
                            <div className="d-flex align-items-center gap-2">
                              <Button variant="outline-primary" size="sm" onClick={() => { setSelectedMatch(match); setViewMode('match-detail'); }} style={{ borderRadius: '6px', fontSize: '12px', fontWeight: 500, padding: '4px 12px', borderColor: '#0d6efd' }}>{questionCount ? 'Manage' : 'Add'}</Button>
                              {match.campaignId && match.predictionStatus === 'Live' && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleCompleteCampaign(match.campaignId)}
                                  disabled={completingCampaignId === match.campaignId || !canCompleteMatchPrediction}
                                  style={{ borderRadius: '6px', fontSize: '12px', fontWeight: 500, padding: '4px 12px' }}
                                >
                                  {completingCampaignId === match.campaignId ? 'Completing...' : 'Mark Completed'}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : renderQuestionsTable([])
          ) : renderQuestionsTable(activeQuestions)}
        </div>
      </div>
	    </div>
	    {renderQuestionDeleteModal()}
    {renderResultModal()}
	    </>
  );
};

export default PredictionsManagement;
